import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  setToken,
  setUserType,
  setUserData,
  setSubscriptionStatus,
  setCompanyId,
} from "../store/auth";
import { storage } from "../utils/storage";

const SUPPORT_SESSION_KEY = "platform_support_session";

/**
 * Public handoff from Manager → Portal support session.
 * Expects ?token=...&expires_at=...&admin_name=...&company_name=...
 */
export default function SupportSessionAccept() {
  const [params] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    const token = params.get("token");
    const expiresAt = params.get("expires_at");
    const adminName = params.get("admin_name") || "";
    const companyName = params.get("company_name") || "";

    if (!token) {
      setError("Missing support session token.");
      return;
    }

    dispatch(setToken(token));
    dispatch(setUserType("staff"));

    const meta = {
      expires_at: expiresAt,
      admin_name: adminName,
      company_name: companyName,
      display_name: adminName
        ? `Platform Support (${adminName})`
        : "Platform Support",
    };
    storage.localStorage.set(SUPPORT_SESSION_KEY, meta);

    // Fetch profile with the new token so role/modules/branch are hydrated.
    const baseUrl = process.env.REACT_APP_BASE_URL || "";
    fetch(`${baseUrl}/cooperatives/profile?guard=staff`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    })
      .then((res) => res.json())
      .then((json) => {
        const profile = json?.data?.user || json?.data || json?.user || null;
        if (profile) {
          dispatch(setUserData(profile));
          if (profile.company_id) {
            dispatch(setCompanyId(profile.company_id));
          }
          if (json?.data?.subscription || profile.subscription) {
            dispatch(
              setSubscriptionStatus(
                json?.data?.subscription || profile.subscription,
              ),
            );
          }
          if (profile.support_session) {
            storage.localStorage.set(SUPPORT_SESSION_KEY, {
              ...meta,
              ...profile.support_session,
            });
          }
        }
        navigate("/super_admin/dashboard", { replace: true });
      })
      .catch(() => {
        setError(
          "Support token was accepted but profile could not be loaded. Try again from Manager.",
        );
      });
  }, [dispatch, navigate, params]);

  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center px-3">
        <div className="text-center" style={{ maxWidth: 420 }}>
          <h1 className="h4 fw-bold mb-2" style={{ color: "#8b313a" }}>
            Support session failed
          </h1>
          <p className="text-muted mb-0">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center px-3">
      <div className="text-center" aria-busy="true" aria-label="Opening support session">
        <div
          className="spinner-border mb-3"
          role="status"
          style={{ color: "#8b313a" }}
        />
        <p className="fw-semibold mb-1" style={{ color: "#383838" }}>
          Opening support session…
        </p>
        <p className="text-muted small mb-0">You will be redirected shortly.</p>
      </div>
    </div>
  );
}

export { SUPPORT_SESSION_KEY };
